import { Suspense, forwardRef, useRef } from "react"
import { useParams } from "react-router-dom"
import { useNavigate } from "react-router"
import { FormikProps } from "formik"
import { queryKeyFor, useApiQuery } from "../../../../../../core/api/useApiQuery"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { StandardButton } from "../../../../../../shared/components/buttons/StandardButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPLIER_ADMIN_MERCHANTS_LIST_ROUTE, SUPPLIER_ADMIN_ROUTE } from "../../../../../../constants/routes"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../../core/api/errorTypes"
import {
    SupplierMerchantDetailsResponse,
    fetchSupplierMerchantDetails,
    updateMerchant,
    SupplierMerchantQuery,
} from "../supplierMerchantsApi"
import { CreateUpdateMerchantFormValues } from "../supplierMerchantsTypes"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { CreateUpdateMerchantForm } from "../components/CreateUpdateMerchantForm"

const selectMerchantDetails = (data: SupplierMerchantDetailsResponse) => {
    const merchant = data.merchant
    if (!merchant) return null

    const { acquiringDetails } = merchant
    const acquiringDetailsValue = acquiringDetails &&
        "acquiring" in acquiringDetails && {
            acquiring: {
                type: acquiringDetails.acquiring.type,
                salesChannels: acquiringDetails.acquiring.salesChannel,
                defaultCurrencyCode: acquiringDetails.acquiring.defaultCurrencyCode,
                bankAccount: {
                    refId: acquiringDetails.acquiring.bankAccount.refId,
                },
                industryCodes: acquiringDetails.acquiring.industryCodes.map((item) => item.refId),
            },
        }
    const bankAccountDetails =
        acquiringDetails && "acquiring" in acquiringDetails
            ? {
                  status: acquiringDetails.acquiring.bankAccount.status,
                  bankName: acquiringDetails.acquiring.bankAccount.bankName,
                  accountIdentifier: acquiringDetails.acquiring.bankAccount.accountIdentifier,
                  industryCodes: acquiringDetails.acquiring.industryCodes.map((item) => item.code),
              }
            : undefined
    const initialValues = {
        caseNumber: merchant.caseNumber,
        name: merchant.name,
        address: {
            ...merchant.address,
            street: merchant.address?.street || "",
            countryCode: merchant.address?.country?.code || "DK",
        },
        openingHours: merchant?.openingHours,
        contactDetails: merchant.contactDetails,
        isActive: merchant.isActive,
        acquiringDetails: acquiringDetailsValue || {},
    }
    return {
        initialValues,
        bankAccountDetails,
        history: merchant?.history,
        subscriptionDetailsInfo: merchant?.subscriptionDetails,
    }
}

const MerchantContent = forwardRef<FormikProps<CreateUpdateMerchantFormValues>>((props, ref) => {
    const { merchantId } = useParams()
    const navigate = useNavigate()

    const merchantDetailsResult = useApiQuery(fetchSupplierMerchantDetails, {
        queryName: SupplierMerchantQuery.DETAILS,
        dependencies: {
            merchantId: merchantId ?? "",
        },
        select: selectMerchantDetails,
        suspense: true,
        useErrorBoundary: true,
    })

    const { mutateAsync: callCreateMerchant } = useApiMutation(updateMerchant, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [SupplierMerchantQuery.LIST] })
            await context.queryClient.invalidateQueries({
                queryKey: queryKeyFor(SupplierMerchantQuery.DETAILS, { merchantId: params.merchantId }),
            })
            navigate(SUPPLIER_ADMIN_MERCHANTS_LIST_ROUTE)
        },
    })

    return (
        <CreateUpdateMerchantForm
            ref={ref}
            initialValues={merchantDetailsResult.data?.initialValues}
            bankAccountDetails={merchantDetailsResult.data?.bankAccountDetails}
            onSubmit={(data) => {
                merchantId && callCreateMerchant({ ...data, merchantId: merchantId })
            }}
            history={merchantDetailsResult.data?.history}
            subscriptionDetails={merchantDetailsResult.data?.subscriptionDetailsInfo}
        />
    )
})

export function SupplierMerchantEdit() {
    const merchantFormRef = useRef<FormikProps<CreateUpdateMerchantFormValues>>(null)

    const handleSaveClick = () => {
        if (merchantFormRef.current) {
            merchantFormRef.current?.submitForm()
        }
    }

    return (
        <>
            <LocalizedStrict id="supplier-admin-merchant-edit-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPLIER_MERCHANTS_EDIT}
                    title="Edit merchant"
                    rightElement={
                        <LocalizedStrict id="supplier-admin-merchants-save-button">
                            <StandardButton onClick={handleSaveClick}>Save</StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="supplier-nav-item-home">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-merchants">
                            <Breadcrumb>Merchants</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-merchants">
                            <BreadcrumbLink to={SUPPLIER_ADMIN_MERCHANTS_LIST_ROUTE}>Merchants</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="supplier-admin-nav-item-merchants-edit">
                            <Breadcrumb>Edit merchant</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <MerchantContent ref={merchantFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

